<template>
  <div class="relative w-full">
    <div
      id="multiselect-container"
      class="flex min-h-[2.5rem] cursor-pointer flex-wrap items-center gap-1 rounded-lg border p-2"
      :class="{
        'open-multiselect-dropdown': isOpen,
      }"
      @click="toggleDropdown"
    >
      <template v-if="selectedOptions.length">
        <span
          v-for="(option, index) in selectedOptions"
          :key="index"
          class="multiselect-pill flex items-center rounded bg-blue-100 px-2 py-1 text-sm text-blue-800"
        >
          {{ option.label }}
          <button
            class="ml-1 text-xs text-steel-500"
            @click.stop="removeOption(option)"
          >
            ✕
          </button>
        </span>
      </template>

      <div
        v-else-if="isSearchable"
        class="relative w-full"
      >
        <input
          :value="inputSearch"
          class="h-7 !border-none text-steel-900 !outline-none"
          @input="(e) => $emit('input-change', e)"
        />

        <button
          v-if="inputSearch?.length > 0"
          @click="$emit('input-change', '')"
          class="absolute right-8 top-2 text-xs text-steel-500"
        >
          ✕
        </button>
      </div>

      <span
        v-else
        class="text-sm text-gray-400"
      >
        {{ placeholder }}
      </span>

      <img
        class="absolute right-3 opacity-50"
        src="@/assets/arrow-down-black.svg"
        alt="carrot"
      />
    </div>

    <div
      v-if="isOpen"
      ref="dropdownRef"
      class="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded border bg-white shadow"
    >
      <div
        v-for="option in options"
        :key="option.value"
        class="multiselect-option relative flex cursor-pointer items-center p-2 text-sm text-steel-900 hover:bg-gray-100"
        :class="
          isSelected(option)
            ? 'border-l-4 border-sun-400 bg-sun-100 font-semibold'
            : 'pl-3'
        "
        @click.stop="toggleOption(option)"
      >
        <input
          type="checkbox"
          class="mr-2 hidden"
          :checked="isSelected(option)"
          @change.prevent
        />

        <span class="checkmark hidden"></span>
        {{ option.label }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useClickAway } from '@/utils/useClickAway';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  options: {
    type: Array,
    required: true, // array of { label, value }
  },
  placeholder: {
    type: String,
    default: 'Select options...',
  },
  singleOption: {
    type: Boolean,
    default: false,
  },
  isSearchable: {
    type: Boolean,
    default: false,
  },
  inputSearch: {
    type: String,
    required: false,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'input-change']);

const isOpen = ref(false);

const selectedOptions = computed(() =>
  props.options.filter((option) => props.modelValue.includes(option.value))
);

function toggleDropdown() {
  isOpen.value = !isOpen.value;
}

function toggleOption(option) {
  let newValue = [...props.modelValue];
  const index = newValue.indexOf(option.value);

  if (index > -1) {
    newValue.splice(index, 1);
  } else {
    if (props.singleOption) {
      newValue = [option.value];
      isOpen.value = false;
    } else {
      newValue.push(option.value);
    }
  }

  emit('update:modelValue', newValue);
}

function removeOption(option) {
  emit(
    'update:modelValue',
    props.modelValue.filter((v) => v !== option.value)
  );
}

function isSelected(option) {
  return props.modelValue.includes(option.value);
}

const dropdownRef = ref();
useClickAway(dropdownRef, (e) => {
  if (e.target.id !== 'multiselect-container') {
    isOpen.value = false;
  }
});
</script>
